const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];
const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

// Date helpers — all dates as YYYY-MM-DD strings, no timezone issues
const pad = (n) => String(n).padStart(2, '0');
const toDateStr = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const todayStr = () => { const n = new Date(); return toDateStr(n.getFullYear(), n.getMonth(), n.getDate()); };

const getFirstDayOfMonth = (year, month) => {
  const d = new Date(year, month, 1).getDay();
  return d === 0 ? 6 : d - 1; // Monday = 0
};
const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

/**
 * Find the slot that covers a given date string.
 * Slots have startDate/endDate as YYYY-MM-DD.
 */
const findSlotForDay = (dateStr, slots) => {
  for (const slot of slots) {
    const start = slot.startDate?.split('T')[0] || slot.startDate;
    const end = slot.endDate?.split('T')[0] || slot.endDate;
    if (dateStr >= start && dateStr <= end) return slot;
  }
  return null;
};

/**
 * Color classes for each status — applied as background to the whole cell.
 */
const STATUS_STYLES = {
  available: 'bg-accent/20 text-accent',
  booked: 'bg-status-pending/30 text-status-pending',
  pending: 'bg-status-pending/15 text-status-pending',
  blocked: 'bg-status-cancelled/20 th-text-muted',
};

/**
 * Check if a dateStr falls within a range { start, end } (YYYY-MM-DD strings).
 */
const isInRange = (dateStr, range) => {
  if (!range || !range.start) return false;
  const end = range.end || range.start;
  const lo = range.start <= end ? range.start : end;
  const hi = range.start <= end ? end : range.start;
  return dateStr >= lo && dateStr <= hi;
};

export default function AvailabilityCalendar({
  slots = [],
  month = new Date().getMonth(),
  year = new Date().getFullYear(),
  onMonthChange = () => {},
  mode = 'readonly',
  onDayClick = () => {},
  onSlotClick = null, // callback(slot) — for readonly mode, when clicking a slot
  selectedDates = new Set(),
  selectedRange = null, // { start: 'YYYY-MM-DD', end: 'YYYY-MM-DD' | null }
}) {
  const today = todayStr();
  const firstDay = getFirstDayOfMonth(year, month);
  const daysInMonth = getDaysInMonth(year, month);
  const prevDays = getDaysInMonth(year, month - 1);

  // Build grid: prev month padding + current month + next month padding
  const cells = [];
  for (let i = firstDay - 1; i >= 0; i--) {
    cells.push({ day: prevDays - i, dateStr: null, outside: true });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, dateStr: toDateStr(year, month, d), outside: false });
  }
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    cells.push({ day: d, dateStr: null, outside: true });
  }

  const goPrev = () => onMonthChange(month === 0 ? 11 : month - 1, month === 0 ? year - 1 : year);
  const goNext = () => onMonthChange(month === 11 ? 0 : month + 1, month === 11 ? year + 1 : year);

  return (
    <div className="w-full border th-border th-bg-surface p-4">
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={goPrev} className="th-btn px-2 py-1 text-sm">&lt;</button>
        <span className="font-mono uppercase tracking-widest text-sm th-text">
          {MONTHS_FR[month]} {year}
        </span>
        <button onClick={goNext} className="th-btn px-2 py-1 text-sm">&gt;</button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-px mb-1">
        {DAYS_FR.map((d) => (
          <div key={d} className="text-center text-[10px] font-mono th-text-muted uppercase tracking-widest py-1">
            {d}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-px">
        {cells.map((cell, i) => {
          if (cell.outside) {
            return (
              <div key={i} className="h-12 flex items-center justify-center text-xs font-mono th-text-muted opacity-20">
                {cell.day}
              </div>
            );
          }

          const slot = findSlotForDay(cell.dateStr, slots);
          const isToday = cell.dateStr === today;
          const isSelected = selectedDates.has(cell.dateStr) || isInRange(cell.dateStr, selectedRange);
          const isPast = cell.dateStr < today;
          const statusStyle = slot ? STATUS_STYLES[slot.status] || '' : '';

          return (
            <button
              key={cell.dateStr}
              onClick={() => {
                if (mode === 'interactive' && slot && onSlotClick) onSlotClick(slot);
                else if (mode === 'interactive') onDayClick(cell.dateStr);
                else if (slot && onSlotClick) onSlotClick(slot);
              }}
              disabled={mode === 'readonly' && !slot && !onSlotClick}
              className={[
                'h-12 flex items-center justify-center text-xs font-mono relative transition-colors duration-100',
                // Status background — the main visual feedback
                slot ? statusStyle : 'th-text',
                // Past day dimming
                isPast && !slot ? 'opacity-40' : '',
                // Today underline
                isToday ? 'border-b-2 border-accent' : '',
                // Selection ring
                isSelected ? 'ring-2 ring-accent ring-inset' : '',
                // Interactive hover or readonly slot click
                mode === 'interactive' && !isPast ? 'hover:bg-accent/10 cursor-pointer' : '',
                mode === 'readonly' && slot && onSlotClick ? 'hover:ring-2 hover:ring-accent/50 hover:ring-inset cursor-pointer' : '',
              ].join(' ')}
            >
              {cell.day}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-4 text-[10px] font-mono th-text-muted uppercase tracking-widest">
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 bg-accent/40" /> Disponible
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 bg-status-pending/50" /> Réservé
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 bg-status-pending/25" /> En attente
        </span>
        {mode === 'interactive' && (
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 bg-status-cancelled/40" /> Bloqué
          </span>
        )}
      </div>
    </div>
  );
}
